import { ApiResponse } from '../models/api-response';
import { callApi } from './api.service';

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

export interface CareerAdvisorAvailability {
  /** Whether the coaching session should replace the tutorial video. */
  available: boolean;
  /** Whether the workspace hired the advisor. */
  enabled: boolean;
  agent_name: string;
  /** Prep sessions the candidate has already used. */
  sessions_used: number;
  /** Prep sessions still available for this candidate. */
  sessions_remaining: number;
  /** Hard cap on prep sessions per candidate. */
  session_limit: number;
}

export interface CoachingTokenResponse {
  token: string;
  webrtc_url: string;
}

export interface CoachingTranscriptTurn {
  role: 'user' | 'assistant';
  text: string;
}

export interface CoachingDimension {
  name: string;
  score: number;
  evidence: string;
  feedback: string;
}

/** Same shape as the sales-demo SalesEvaluation, so SessionResults renders it. */
export interface CoachingEvaluation {
  dimensions: CoachingDimension[];
  summary: string;
  strengths: string[];
  improvements: string[];
}

/** Candidate-facing, token-gated — no auth header needed. */
export const getCareerAdvisorAvailability = async (
  invitationToken: string,
): Promise<ApiResponse<CareerAdvisorAvailability>> => {
  return callApi<CareerAdvisorAvailability>(
    `${apiServerUrl}/api/interviews/${invitationToken}/career-advisor`,
    { method: 'GET', headers: { 'content-type': 'application/json' } },
  );
};

/** Mint the Azure Realtime ephemeral token for a coaching session. */
export async function fetchCoachingToken(
  invitationToken: string,
  language: string = 'en',
): Promise<CoachingTokenResponse> {
  const response = await callApi<CoachingTokenResponse>(
    `${apiServerUrl}/api/interviews/${invitationToken}/career-advisor/token`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ language }),
    },
  );
  if (response.error) throw new Error(response.error);
  return response.data!;
}

/** Score the candidate's readiness from the coaching transcript. */
export async function evaluateCoaching(
  invitationToken: string,
  transcript: CoachingTranscriptTurn[],
): Promise<CoachingEvaluation | null> {
  const response = await callApi<{ evaluation: CoachingEvaluation | null }>(
    `${apiServerUrl}/api/interviews/${invitationToken}/career-advisor/evaluate`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ transcript }),
    },
  );
  if (response.error) throw new Error(response.error);
  return response.data?.evaluation ?? null;
}

/* ---- Learning resources ------------------------------------------------ */

export interface LearningResource {
  id: string;
  title: string;
  url: string;
  rationale: string | null;
  skill_area: string | null;
  source: string;
  opened: boolean;
  completed: boolean;
}

export interface LearningResourcesResult {
  /** True while the background generation task has not produced rows yet. */
  generating: boolean;
  resources: LearningResource[];
}

/** List the candidate's learning resources (generated after coaching). */
export async function getLearningResources(
  invitationToken: string,
): Promise<LearningResourcesResult> {
  const response = await callApi<LearningResourcesResult>(
    `${apiServerUrl}/api/interviews/${invitationToken}/career-advisor/resources`,
    { method: 'GET', headers: { 'content-type': 'application/json' } },
  );
  if (response.error) throw new Error(response.error);
  return response.data ?? { generating: false, resources: [] };
}

/** Record that the candidate opened a resource. */
export async function openLearningResource(
  invitationToken: string,
  resourceId: string,
): Promise<LearningResource> {
  const response = await callApi<{ resource: LearningResource }>(
    `${apiServerUrl}/api/interviews/${invitationToken}/career-advisor/resources/${resourceId}/open`,
    { method: 'POST', headers: { 'content-type': 'application/json' } },
  );
  if (response.error) throw new Error(response.error);
  return response.data!.resource;
}

/** Mark a resource as done, or undo it. */
export async function completeLearningResource(
  invitationToken: string,
  resourceId: string,
  completed: boolean,
): Promise<LearningResource> {
  const response = await callApi<{ resource: LearningResource }>(
    `${apiServerUrl}/api/interviews/${invitationToken}/career-advisor/resources/${resourceId}/complete`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ completed }),
    },
  );
  if (response.error) throw new Error(response.error);
  return response.data!.resource;
}
